import {
  DESPEDIDA_PADRAO,
  type MensagemRapidaResumo,
  type RespostaPessoalForm,
} from '@mymaxx/shared';
import { Aviso, Botao, CampoTexto, Modal } from '@mymaxx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ErroApi } from '../lib/api';
import { atendimentoApi } from '../lib/atendimento-api';
import { arrumarAtalho } from '../lib/atalho';
import { useSessao } from '../lib/sessao';

const VAZIA: RespostaPessoalForm = { atalho: '', titulo: '', conteudo: '' };

/**
 * As respostas rápidas do próprio atendente — o roteiro dele.
 *
 * Existem separadas das respostas da casa porque servem a coisas diferentes: as da casa
 * padronizam o que a empresa diz e são do admin; estas são o jeito de cada um escrever o
 * que repete o dia todo, e esperar aprovação para isso só faria ninguém usar.
 */
export function MinhasRespostas({ onFechar }: { onFechar: () => void }) {
  const qc = useQueryClient();
  const catalogos = useQuery({
    queryKey: ['catalogos-atendimento'],
    queryFn: atendimentoApi.catalogos,
  });
  const [editando, setEditando] = useState<{ id: string | null; form: RespostaPessoalForm } | null>(
    null,
  );
  const [erro, setErro] = useState<string | null>(null);

  const recarregar = () => {
    void qc.invalidateQueries({ queryKey: ['catalogos-atendimento'] });
    setEditando(null);
    setErro(null);
  };
  const tratarErro = (e: unknown) =>
    setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui salvar');

  const salvar = useMutation({
    mutationFn: ({ id, form }: { id: string | null; form: RespostaPessoalForm }) =>
      id
        ? atendimentoApi.editarRespostaPessoal(id, form)
        : atendimentoApi.criarRespostaPessoal(form),
    onSuccess: recarregar,
    onError: tratarErro,
  });
  const apagar = useMutation({
    mutationFn: (id: string) => atendimentoApi.apagarRespostaPessoal(id),
    onSuccess: recarregar,
    onError: tratarErro,
  });

  const minhas = (catalogos.data?.mensagensRapidas ?? []).filter((m) => m.pessoal);

  if (editando) {
    return (
      <Modal
        aberto
        titulo={editando.id ? 'Editar resposta' : 'Nova resposta'}
        onFechar={() => setEditando(null)}
        acoes={
          <Botao carregando={salvar.isPending} onClick={() => salvar.mutate(editando)}>
            Salvar
          </Botao>
        }
      >
        <div className="space-y-3">
          <CampoTexto
            rotulo="Atalho"
            autoFocus
            value={editando.form.atalho}
            onChange={(e) =>
              setEditando({
                ...editando,
                form: { ...editando.form, atalho: arrumarAtalho(e.target.value) },
              })
            }
            ajuda="Escreva sem a barra. No chat você digita /atalho e o texto entra."
          />
          <CampoTexto
            rotulo="Título"
            value={editando.form.titulo}
            onChange={(e) =>
              setEditando({ ...editando, form: { ...editando.form, titulo: e.target.value } })
            }
            ajuda="Como aparece na lista de respostas."
          />
          <label className="block text-sm font-medium">
            Texto
            <textarea
              rows={5}
              value={editando.form.conteudo}
              onChange={(e) =>
                setEditando({ ...editando, form: { ...editando.form, conteudo: e.target.value } })
              }
              className="mt-1 w-full resize-none rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
            />
          </label>
          {erro && <Aviso tipo="erro">{erro}</Aviso>}
        </div>
      </Modal>
    );
  }

  return (
    <Modal aberto titulo="Minhas respostas rápidas" onFechar={onFechar}>
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm text-neutral-500">
            Só você vê e usa estas. As respostas da casa continuam aparecendo junto, na hora de
            responder.
          </p>
          <Botao
            onClick={() => {
              setEditando({ id: null, form: { ...VAZIA } });
              setErro(null);
            }}
          >
            <Plus size={15} /> Nova
          </Botao>
        </div>

        {erro && <Aviso tipo="erro">{erro}</Aviso>}

        <MinhaDespedida />

        {minhas.length === 0 && !catalogos.isPending && (
          <p className="rounded-lg border border-dashed border-neutral-300 px-4 py-6 text-center text-sm text-neutral-500 dark:border-neutral-700">
            Você ainda não tem respostas suas. Crie uma para o que você mais repete.
          </p>
        )}

        <ul className="max-h-[55vh] space-y-1 overflow-y-auto">
          {minhas.map((m) => (
            <Linha
              key={m.id}
              resposta={m}
              onEditar={() =>
                setEditando({
                  id: m.id,
                  form: { atalho: m.atalho, titulo: m.titulo, conteudo: m.conteudo },
                })
              }
              onApagar={() => apagar.mutate(m.id)}
            />
          ))}
        </ul>
      </div>
    </Modal>
  );
}

function Linha({
  resposta,
  onEditar,
  onApagar,
}: {
  resposta: MensagemRapidaResumo;
  onEditar: () => void;
  onApagar: () => void;
}) {
  return (
    <li className="flex items-start gap-3 rounded-lg border border-neutral-200 px-3 py-2 dark:border-neutral-700">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">
          {resposta.titulo}{' '}
          <span className="font-mono text-xs text-neutral-500">/{resposta.atalho}</span>
        </p>
        <p className="truncate text-xs text-neutral-500">{resposta.conteudo}</p>
      </div>
      <div className="flex shrink-0 gap-1">
        <Botao variante="secundario" onClick={onEditar} aria-label="Editar">
          <Pencil size={14} />
        </Botao>
        <Botao variante="secundario" onClick={onApagar} aria-label="Apagar">
          <Trash2 size={14} />
        </Botao>
      </div>
    </li>
  );
}

/**
 * A despedida de quem encerra.
 *
 * Mora aqui, junto das respostas rápidas, porque é a mesma coisa: texto que o atendente
 * repete o dia todo e escreve do jeito dele. A tela de encerrar já vinha com uma frase
 * pronta, e cada um a reescrevia à mão em todo atendimento (pedido de 03/10).
 *
 * Em branco não é erro: é voltar para a frase da casa.
 */
function MinhaDespedida() {
  const { usuario, definirUsuario } = useSessao();
  const [texto, setTexto] = useState(usuario?.despedida ?? '');
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const salvar = useMutation({
    mutationFn: () => atendimentoApi.definirDespedida(texto),
    onSuccess: ({ despedida }) => {
      // A sessão guarda a despedida: é de lá que a tela de encerrar a lê, já preenchida.
      if (usuario) definirUsuario({ ...usuario, despedida });
      setTexto(despedida ?? '');
      setErro(null);
      setAviso(despedida ? 'Despedida salva.' : 'Voltou para a mensagem da casa.');
    },
    onError: (e) => {
      setAviso(null);
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui salvar');
    },
  });

  return (
    <div className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
      <p className="text-sm font-medium">Minha mensagem de encerramento</p>
      <p className="mb-2 text-xs text-neutral-500">
        É o que o cliente recebe quando você encerra. Em branco, vale a da casa: “{DESPEDIDA_PADRAO}
        ”
      </p>
      <textarea
        rows={2}
        value={texto}
        aria-label="Minha mensagem de encerramento"
        placeholder={DESPEDIDA_PADRAO}
        onChange={(e) => {
          setTexto(e.target.value);
          setAviso(null);
        }}
        className="w-full resize-none rounded border border-neutral-300 px-2 py-1.5 text-sm dark:border-neutral-700 dark:bg-neutral-900"
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-xs text-green-700 dark:text-green-400">{aviso}</span>
        <span className="text-xs text-red-600">{erro}</span>
        <Botao
          variante="secundario"
          carregando={salvar.isPending}
          disabled={(usuario?.despedida ?? '') === texto.trim()}
          onClick={() => salvar.mutate()}
        >
          Salvar
        </Botao>
      </div>
    </div>
  );
}
